import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Avatar } from '../components/Logo';
import { STAGES, formatRelative, stripHtml } from '../constants/users';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileDashboardPage from './MobileDashboardPage';
import { CalendlyWidget } from '../components/CalendlyWidget';
import { ElevateInfluenceCard } from '../components/ElevateInfluenceCard';
import {
  CheckSquare, FileText, Calendar, Calendar as CalendarIconAlias, MessageCircle, ChevronRight, ChevronLeft, Sparkles,
  Clock, AlertTriangle, Sun, Moon, ProjectIcon, Check, X, Briefcase, Target,
} from '../components/icons';
import { localToday, localISO, parseLocalDate } from '../lib/dates';

export default function DashboardPage() {
  // Iter61af — dedicated mobile Accueil (user-provided HTML redesign)
  const isMobile = useIsMobile();
  if (isMobile) return <MobileDashboardPage />;
  return <DashboardPageDesktop />;
}

function DashboardPageDesktop() {
  const { user, isFounder, isManager } = useAuth();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [convs, setConvs] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [myStatus, setMyStatus] = useState({ available: null }); // null = auto
  const [loading, setLoading] = useState(true);

  const [activity, setActivity] = useState({ since: null, events: [] });

  const reload = () => {
    Promise.all([
      api.get('/tasks').then((r) => r.data).catch(() => []),
      isFounder ? api.get('/notes').then((r) => r.data).catch(() => []) : Promise.resolve([]),
      api.get('/messages/conversations').then((r) => r.data).catch(() => []),
      api.get('/users').then((r) => r.data).catch(() => []),
      api.get('/activity-feed').then((r) => r.data).catch(() => ({ since: null, events: [] })),
      api.get('/projects').then((r) => r.data).catch(() => []),
      !isFounder ? api.get(`/users/${user.id}/missions`).then((r) => r.data).catch(() => null) : Promise.resolve(null),
    ]).then(([t, n, c, u, a, p, m]) => {
      setTasks(t); setNotes(n); setConvs(c); setUsers(u); setActivity(a); setProjects(p);
      if (m) setMyStatus({ available: m.available });
      setLoading(false);
    });
  };

  useEffect(reload, [isFounder, user?.id]);

  // Self-availability toggle (monteur)
  const toggleAvailability = async (newVal) => {
    setMyStatus({ available: newVal });
    try {
      await api.patch('/me/availability', { available: newVal });
      toast.success(newVal ? 'Tu es marqué disponible' : 'Tu es marqué indisponible');
    } catch { toast.error('Erreur'); }
  };

  // Greeting
  const hour = new Date().getHours();
  const isMorning = hour >= 6 && hour < 18;
  const greeting =
    hour < 6 ? 'Toujours debout' :
    hour < 12 ? 'Content de te revoir' :
    hour < 18 ? 'On continue de tout casser' :
    'Belle soirée';

  const dateLabel = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  // Personal tasks only (user explicitly asked: overview counter = MY tasks)
  const myTasks = useMemo(() => tasks.filter((t) =>
    t.assigned_to === user?.id
    || t.assigned_to_derush === user?.id
    || t.assigned_to_montage === user?.id
    || (t.assignees || []).includes(user?.id)
  ), [tasks, user?.id]);

  // Iter43 — Vidéos à rendre : the 2 most urgent video tasks not yet posted online.
  // Sorted by due_date ascending (soonest first). Only for the current user's tasks.
  const todayVideos = useMemo(() => {
    return myTasks
      .filter((t) => t.kind === 'video' && !t.posted_online && t.stage !== 'done')
      .sort((a, b) => (a.due_date || '9999-12-31').localeCompare(b.due_date || '9999-12-31'))
      .slice(0, 2);
  }, [myTasks]);

  // Today's tasks + overdue (scoped to MY tasks). Iter61l — local timezone
  const todayStr = localToday();
  const todayTasks = myTasks.filter((t) =>
    t.stage !== 'done' && t.due_date && t.due_date.slice(0, 10) <= todayStr
  );
  const overdueCount = todayTasks.filter((t) =>
    t.due_date && t.due_date.slice(0, 10) < todayStr
  ).length;

  // Pipeline stats — MY tasks
  const byStage = { todo: 0, in_progress: 0, review: 0, done: 0 };
  myTasks.forEach((t) => { byStage[t.stage] = (byStage[t.stage] || 0) + 1; });
  const activeCount = byStage.todo + byStage.in_progress + byStage.review;

  const recentNotes = [...notes].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 4);

  return (
    <div
      data-testid="overview-fullscreen"
      className="overview-fullscreen"
      style={{
        position: 'relative',
        width: '100%',
        minHeight: 'calc(100vh - 0px)',
        display: 'flex', flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
      }}
    >
      {/* Iter62d — ASA logo watermark for unified iOS Dark Premium look */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        <img src="/asa-logo.png" alt="" style={{ width: 480, opacity: 0.045, filter: 'drop-shadow(0 0 60px rgba(62,123,250,0.5))' }} />
      </div>
      <div style={{
        position: 'absolute', top: -80, right: -120, width: 460, height: 460,
        background: 'radial-gradient(circle, rgba(127,173,255,0.18) 0%, rgba(62,123,250,0.06) 45%, transparent 70%)',
        filter: 'blur(24px)', pointerEvents: 'none', zIndex: 0,
      }} />
      {/* Fullscreen carousel — this is the ONLY content on overview */}
      {isFounder ? (
        <OverviewCarousel
          mode={isManager ? 'manager' : 'founder'}
          user={user}
          todayTasks={todayTasks}
          myTasks={myTasks}
          byStage={byStage}
          overdueCount={overdueCount}
          nav={nav}
        />
      ) : user?.role === 'client' ? (
        <OverviewCarousel
          mode="client"
          user={user}
          todayTasks={todayTasks}
          myTasks={myTasks}
          byStage={byStage}
          overdueCount={overdueCount}
          nav={nav}
        />
      ) : (
        <OverviewCarousel
          mode="monteur"
          user={user}
          todayTasks={todayTasks}
          myTasks={myTasks}
          byStage={byStage}
          overdueCount={overdueCount}
          projects={projects}
          available={myStatus.available}
          onToggleAvailable={toggleAvailability}
          nav={nav}
        />
      )}
    </div>
  );
}

function Card({ children, onSeeAll, ...rest }) {
  return (
    <div className="asa-card" style={{ padding: 18, position: 'relative' }} {...rest}>
      {children}
      {onSeeAll && (
        <button onClick={onSeeAll} style={{
          position: 'absolute', top: 18, right: 16,
          display: 'flex', alignItems: 'center', gap: 4,
          color: 'var(--p)', fontFamily: 'Sora', fontWeight: 600, fontSize: '0.7rem',
        }}>
          Voir tout <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}

function Header({ icon: Icon, title, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
      <div style={{
        width: 34, height: 34, borderRadius: 10,
        background: 'var(--pbg)', border: '1px solid var(--pbd)',
        display: 'grid', placeItems: 'center',
      }}>
        <Icon size={16} color="var(--p)" strokeWidth={1.7} />
      </div>
      <div>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.94rem', color: 'var(--t1)' }}>{title}</div>
        <div className="asa-eyebrow" style={{ marginTop: 2, fontSize: '0.62rem' }}>{sub}</div>
      </div>
    </div>
  );
}

function StatBlock({ value, label, color }) {
  return (
    <div style={{
      padding: '12px 10px', borderRadius: 12,
      background: 'var(--sf2)', border: '1px solid var(--bd)',
    }}>
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t3)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Skel({ n = 3 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="shimmer" style={{ height: 50, borderRadius: 10 }} />
      ))}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div style={{
      padding: '18px 0 4px', textAlign: 'center',
      color: 'var(--t3)', fontFamily: 'Lexend', fontSize: '0.84rem', fontStyle: 'italic',
    }}>{text}</div>
  );
}


// ---------------------------------------------------------------------------
// ActivityFeedCard — "Depuis ta dernière visite" (founder view)
// ---------------------------------------------------------------------------
function ActivityFeedCard({ activity, users, nav }) {
  const events = activity.events || [];
  if (events.length === 0) return null;

  const iconFor = (type) => {
    if (type === 'task_done') return { icon: CheckSquare, color: '#34D399', label: 'Étape terminée' };
    if (type === 'note_updated') return { icon: FileText, color: '#5BBEFC', label: 'Note modifiée' };
    if (type === 'message_new') return { icon: MessageCircle, color: '#A78BFA', label: 'Nouveau message' };
    if (type === 'project_request') return { icon: AlertTriangle, color: '#FF6B6B', label: 'Demande de projet' };
    return { icon: Sparkles, color: '#8A9DB8', label: 'Activité' };
  };

  const navigateFor = (e) => {
    if (e.type === 'task_done') nav('/tasks');
    else if (e.type === 'note_updated') nav('/notes');
    else if (e.type === 'message_new') nav('/messages');
    else if (e.type === 'project_request') nav('/projects');
  };

  return (
    <Card data-testid="activity-feed-card">
      <Header icon={Sparkles} title="Depuis ta dernière visite" sub={`${events.length} événement${events.length > 1 ? 's' : ''}`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
        {events.slice(0, 6).map((e) => {
          const m = iconFor(e.type);
          const Icon = m.icon;
          const actor = users.find((u) => u.id === e.actor_id);
          return (
            <button
              key={e.id}
              data-testid={`activity-${e.type}-${e.id}`}
              onClick={() => navigateFor(e)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                background: 'var(--sf2)', border: '1px solid var(--bd)',
                textAlign: 'left', width: '100%',
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: `${m.color}1F`, border: `1.5px solid ${m.color}44`,
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <Icon size={13} color={m.color} strokeWidth={1.8} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: '0.84rem', color: 'var(--t1)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {e.title}
                </div>
                <div style={{
                  fontFamily: 'Lexend', fontSize: '0.7rem', color: 'var(--t3)',
                  display: 'flex', alignItems: 'center', gap: 5,
                  overflow: 'hidden',
                }}>
                  <span style={{ color: m.color, fontWeight: 600 }}>{m.label}</span>
                  {actor && <><span>·</span><span>{actor.name}</span></>}
                  {e.project_name && <><span>·</span><span>{e.project_name}</span></>}
                  {e.conversation_name && <><span>·</span><span>{e.conversation_name}</span></>}
                </div>
              </div>
              <ChevronRight size={13} color="var(--t3)" />
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ============================================================================
// MonteurDashboardSection — Tailored block for editors
// ============================================================================
function MonteurDashboardSection({ tasks, projects, available, onToggleAvailable, nav }) {
  // Compute video tasks (4-block layout) with current block
  const videoTasks = useMemo(() => {
    return tasks.filter((t) => {
      const tags = Array.isArray(t.tags) ? t.tags : [];
      const isVideo = t.kind === 'video' || tags.includes('video_deliverable') ||
                      t.event_type === 'content' || !!t.concept_id;
      return isVideo && t.stage !== 'done' && !t.archived;
    });
  }, [tasks]);

  const computeBlock = (t) => {
    if (t.publication_done) return { idx: 4, label: 'Bloc 4 — Publication ✓', pct: 100, color: '#34D399' };
    if (t.concept_validated) return { idx: 4, label: 'Bloc 4 — Publication', pct: 75, color: '#A78BFA' };
    if (t.tech_validated)    return { idx: 3, label: 'Bloc 3 — Vidéo montée', pct: 50, color: '#5BBEFC' };
    if (t.consignes_validated) return { idx: 2, label: 'Bloc 2 — Consignes tech', pct: 25, color: '#F5A623' };
    return { idx: 1, label: 'Bloc 1 — Consignes', pct: 5, color: '#EC4899' };
  };

  const myProjects = projects.slice(0, 3);
  const isAvail = available !== false;  // default: available

  return (
    <>
      {/* === Availability self-toggle === */}
      <div
        className="asa-card"
        style={{
          padding: 14, display: 'flex', alignItems: 'center', gap: 12,
          background: isAvail
            ? 'linear-gradient(135deg, rgba(52,211,153,0.10), transparent 70%)'
            : 'linear-gradient(135deg, rgba(239,68,68,0.10), transparent 70%)',
          border: `1.5px solid ${isAvail ? 'rgba(52,211,153,0.35)' : 'rgba(239,68,68,0.35)'}`,
        }}
      >
        <div style={{
          width: 38, height: 38, borderRadius: 11,
          background: isAvail ? 'rgba(52,211,153,0.18)' : 'rgba(239,68,68,0.18)',
          border: `1.5px solid ${isAvail ? '#34D399' : '#EF4444'}`,
          display: 'grid', placeItems: 'center',
        }}>
          <Briefcase size={16} color={isAvail ? '#34D399' : '#EF4444'} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: 'var(--t1)' }}>
            Mon statut : {isAvail ? 'Disponible' : 'Indisponible'}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>
            {isAvail
              ? 'Visible par les founders comme prêt à recevoir une mission'
              : 'Visible par les founders comme indisponible'}
          </div>
        </div>
        <button
          data-testid="monteur-avail-toggle"
          onClick={() => onToggleAvailable(!isAvail)}
          style={{
            padding: '8px 14px', borderRadius: 999,
            background: isAvail ? 'rgba(239,68,68,0.13)' : 'var(--p)',
            border: `1.5px solid ${isAvail ? 'rgba(239,68,68,0.35)' : 'var(--pl)'}`,
            color: isAvail ? '#EF4444' : '#fff',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            cursor: 'pointer',
          }}
        >
          {isAvail ? 'Marquer indisponible' : 'Devenir disponible'}
        </button>
      </div>

      {/* === My video tasks with block progress === */}
      <Card onSeeAll={() => nav('/tasks')}>
        <Header
          icon={Sparkles}
          title="Mes vidéos en cours"
          sub={`${videoTasks.length} task${videoTasks.length > 1 ? 's' : ''} · progression bloc-par-bloc`}
        />
        {videoTasks.length === 0 ? (
          <Empty text="Aucune vidéo en cours — repose-toi un peu" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
            {videoTasks.slice(0, 5).map((t) => {
              const blk = computeBlock(t);
              const p = projects.find((x) => x.id === t.project_id);
              return (
                <button
                  key={t.id}
                  data-testid={`monteur-video-${t.id}`}
                  onClick={() => nav('/tasks')}
                  style={{
                    padding: '11px 13px', borderRadius: 11, textAlign: 'left', width: '100%',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
                    {p && (
                      <div style={{
                        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                        background: `${p.color || '#5BBEFC'}1F`,
                        border: `1px solid ${p.color || '#5BBEFC'}55`,
                        display: 'grid', placeItems: 'center',
                      }}>
                        <ProjectIcon kind={p.icon || 'camera'} size={11} color={p.color || '#5BBEFC'} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{t.title}</div>
                      {p && (
                        <div style={{ fontFamily: 'Lexend', fontSize: 10, color: 'var(--t3)' }}>{p.name}</div>
                      )}
                    </div>
                    <span style={{
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: blk.color,
                      padding: '2px 8px', borderRadius: 999,
                      background: `${blk.color}1A`, border: `1px solid ${blk.color}55`,
                      whiteSpace: 'nowrap',
                    }}>{blk.label}</span>
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--sf)', overflow: 'hidden' }}>
                    <div style={{
                      width: `${blk.pct}%`, height: '100%',
                      background: blk.color, transition: 'width 0.5s ease',
                    }} />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* === My projects (quick access) === */}
      {myProjects.length > 0 && (
        <Card onSeeAll={() => nav('/projects')}>
          <Header icon={CheckSquare} title="Mes projets" sub="Accès direct" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
            {myProjects.map((p) => (
              <button
                key={p.id}
                data-testid={`monteur-project-${p.id}`}
                onClick={() => { localStorage.setItem('asa_open_project', p.id); nav('/projects'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '11px 12px', borderRadius: 10,
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                  textAlign: 'left', width: '100%',
                }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: `${p.color || '#5BBEFC'}1F`,
                  border: `1px solid ${p.color || '#5BBEFC'}55`,
                  display: 'grid', placeItems: 'center',
                }}>
                  <ProjectIcon kind={p.icon || 'camera'} size={13} color={p.color || '#5BBEFC'} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{p.name}</div>
                  {p.progress && p.progress.total > 0 && (
                    <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 1 }}>
                      {p.progress.done}/{p.progress.total} tâches · {Math.round((p.progress.ratio || 0) * 100)}%
                    </div>
                  )}
                </div>
                <ChevronRight size={13} color="var(--t3)" />
              </button>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}



// ============================================================================
// OverviewCarousel — 4 swipable slides
//   mode='founder': Ma journée → Objectifs → Tâches → Calendrier
//   mode='monteur': Mon statut → Mes vidéos → Mes projets → Mon calendrier
// ============================================================================
const SLIDES_FOUNDER = [
  { key: 'calendly',  label: 'RDV Calendly' },
  { key: 'day',       label: 'Ma journée' },
  { key: 'elevate',   label: 'Elevate' },
  { key: 'goals',     label: 'Objectifs' },
  { key: 'tasks',     label: 'Tâches' },
  { key: 'calendar',  label: 'Calendrier' },
  { key: 'messages',  label: 'Messages' },
];

// Iter 62bp — Gestionnaire (admin-lite) sees founder slides WITHOUT Elevate.
// Iter 62cj — Calendly slide added for everyone (uses their personal OAuth).
const SLIDES_MANAGER = [
  { key: 'calendly',  label: 'RDV Calendly' },
  { key: 'day',       label: 'Ma journée' },
  { key: 'goals',     label: 'Objectifs' },
  { key: 'tasks',     label: 'Tâches' },
  { key: 'calendar',  label: 'Calendrier' },
  { key: 'messages',  label: 'Messages' },
];

const SLIDES_MONTEUR = [
  { key: 'calendly',  label: 'RDV Calendly' },
  { key: 'status',    label: 'Mon statut' },
  { key: 'videos',    label: 'Mes vidéos' },
  { key: 'projects',  label: 'Mes projets' },
  { key: 'calendar',  label: 'Mon calendrier' },
  { key: 'messages',  label: 'Messages' },
];

// Iter60d — client sees the SAME overview slides as a founder but without messages
// (they don't have access to the /messages page).
const SLIDES_CLIENT = [
  { key: 'calendly',  label: 'RDV Calendly' },
  { key: 'day',       label: 'Ma journée' },
  { key: 'goals',     label: 'Objectifs' },
  { key: 'tasks',     label: 'Tâches' },
  { key: 'calendar',  label: 'Calendrier' },
];

function OverviewCarousel({ mode = 'founder', user, todayTasks, myTasks, byStage, overdueCount, projects, available, onToggleAvailable, nav }) {
  const SLIDES = mode === 'monteur' ? SLIDES_MONTEUR : mode === 'client' ? SLIDES_CLIENT : mode === 'manager' ? SLIDES_MANAGER : SLIDES_FOUNDER;
  const [idx, setIdx] = useState(0);
  const [objectives, setObjectives] = useState([]);
  // Iter 62fi — Si Calendly non connecté, démarre sur la slide "Tâches"
  // (sauf monteur qui part sur "Mon statut"). Évite d'afficher une slide vide.
  const [calendlyConnected, setCalendlyConnected] = useState(null); // null = loading
  const scrollerRef = useRef(null);
  const rafRef = useRef(null);
  const scrolledInitRef = useRef(false);

  useEffect(() => {
    api.get('/calendly/user-status')
      .then((r) => setCalendlyConnected(!!r.data?.connected))
      .catch(() => setCalendlyConnected(false));
  }, []);

  useEffect(() => {
    if (calendlyConnected === null || scrolledInitRef.current) return;
    scrolledInitRef.current = true;
    if (!calendlyConnected && scrollerRef.current) {
      // Priorité : 'tasks' (founder/manager/client) sinon 'videos' (monteur) sinon 'day'
      const prefKey = (mode === 'monteur') ? 'videos' : 'tasks';
      let targetIdx = SLIDES.findIndex((s) => s.key === prefKey);
      if (targetIdx < 0) targetIdx = SLIDES.findIndex((s) => s.key === 'day');
      if (targetIdx > 0) {
        scrollerRef.current.scrollLeft = targetIdx * scrollerRef.current.clientWidth;
        setIdx(targetIdx);
      }
    }
  }, [calendlyConnected, SLIDES, mode]);

  useEffect(() => {
    // Iter60e — Founders AND clients see weekly objectives
    if (mode === 'founder' || mode === 'manager' || mode === 'client') {
      const d = new Date();
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      const weekStart = localISO(d);
      api.get(`/objectives?week_start=${weekStart}`)
        .then((r) => setObjectives(r.data || []))
        .catch(() => setObjectives([]));
    }
  }, [mode]);

  // Update active index as user scrolls the container (native scroll-snap does the work)
  const onScroll = () => {
    if (!scrollerRef.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const next = Math.round(el.scrollLeft / el.clientWidth);
      if (next !== idx && next >= 0 && next < SLIDES.length) setIdx(next);
    });
  };

  const goTo = (i) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTo({
      left: i * scrollerRef.current.clientWidth,
      behavior: 'smooth',
    });
  };
  const goNext = () => goTo(Math.min(SLIDES.length - 1, idx + 1));
  const goPrev = () => goTo(Math.max(0, idx - 1));

  return (
    <div
      data-testid="overview-carousel"
      style={{
        position: 'relative',
        width: '100%',
        height: 'calc(100vh - 0px)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable slides — native horizontal scroll with snap */}
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="overview-scroller"
        style={{
          flex: 1,
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {SLIDES.map((s, i) => (
          <div
            key={s.key}
            style={{
              flex: '0 0 100%', width: '100%', minWidth: '100%',
              scrollSnapAlign: 'start', scrollSnapStop: 'always',
              display: 'grid', placeItems: 'center',
              padding: '40px 32px 28px', boxSizing: 'border-box',
              overflowY: 'auto',
            }}
          >
            {mode === 'founder' && i === 0 && <CalendlySlide />}
            {mode === 'founder' && i === 1 && <DaySlide user={user} todayTasks={todayTasks} overdueCount={overdueCount} nav={nav} />}
            {mode === 'founder' && i === 2 && <ElevateSlide nav={nav} />}
            {mode === 'founder' && i === 3 && <GoalsSlide objectives={objectives} user={user} nav={nav} />}
            {mode === 'founder' && i === 4 && <TasksSlide byStage={byStage} myTasks={myTasks} overdueCount={overdueCount} nav={nav} />}
            {mode === 'founder' && i === 5 && <CalendarSlide myTasks={myTasks} nav={nav} />}
            {mode === 'founder' && i === 6 && <MessagesSlide nav={nav} />}
            {/* Iter 62bp / 62cj — Manager sees Calendly first (personal OAuth), no Elevate */}
            {mode === 'manager' && i === 0 && <CalendlySlide />}
            {mode === 'manager' && i === 1 && <DaySlide user={user} todayTasks={todayTasks} overdueCount={overdueCount} nav={nav} />}
            {mode === 'manager' && i === 2 && <GoalsSlide objectives={objectives} user={user} nav={nav} />}
            {mode === 'manager' && i === 3 && <TasksSlide byStage={byStage} myTasks={myTasks} overdueCount={overdueCount} nav={nav} />}
            {mode === 'manager' && i === 4 && <CalendarSlide myTasks={myTasks} nav={nav} />}
            {mode === 'manager' && i === 5 && <MessagesSlide nav={nav} />}
            {/* Iter 62cj — Monteur (Mathis) sees Calendly first too */}
            {mode === 'monteur' && i === 0 && <CalendlySlide />}
            {mode === 'monteur' && i === 1 && <MyStatusSlide user={user} available={available} onToggleAvailable={onToggleAvailable} todayTasks={todayTasks} myTasks={myTasks} overdueCount={overdueCount} />}
            {mode === 'monteur' && i === 2 && <MyVideosSlide myTasks={myTasks} projects={projects} nav={nav} />}
            {mode === 'monteur' && i === 3 && <MyProjectsSlide projects={projects} myTasks={myTasks} user={user} nav={nav} />}
            {mode === 'monteur' && i === 4 && <CalendarSlide myTasks={myTasks} nav={nav} />}
            {mode === 'monteur' && i === 5 && <MessagesSlide nav={nav} />}
            {/* Iter60e / 62cj — Client overview: Calendly first, then same layout as founder minus messages */}
            {mode === 'client' && i === 0 && <CalendlySlide />}
            {mode === 'client' && i === 1 && <DaySlide user={user} todayTasks={todayTasks} overdueCount={overdueCount} nav={nav} />}
            {mode === 'client' && i === 2 && <GoalsSlide objectives={objectives} user={user} nav={nav} />}
            {mode === 'client' && i === 3 && <TasksSlide byStage={byStage} myTasks={myTasks} overdueCount={overdueCount} nav={nav} />}
            {mode === 'client' && i === 4 && <CalendarSlide myTasks={myTasks} nav={nav} />}
          </div>
        ))}
      </div>

      {/* Nav bar at the bottom: prev · dots · next */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 30px 22px', gap: 12,
        borderTop: '1px solid var(--bd)',
        background: 'var(--bg)',
      }}>
        <button
          data-testid="overview-prev"
          onClick={goPrev}
          disabled={idx === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: 'none',
            color: idx === 0 ? 'var(--t3)' : 'var(--t1)',
            opacity: idx === 0 ? 0.35 : 1,
            cursor: idx === 0 ? 'default' : 'pointer',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
            padding: '8px 12px', borderRadius: 10,
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { if (idx !== 0) e.currentTarget.style.background = 'var(--sf2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <ChevronLeft size={15} />
          {idx > 0 && SLIDES[idx - 1].label}
        </button>

        <div style={{ display: 'flex', gap: 8 }}>
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              data-testid={`overview-dot-${s.key}`}
              onClick={() => goTo(i)}
              style={{
                width: i === idx ? 22 : 7, height: 7, borderRadius: 999,
                background: i === idx ? 'var(--p)' : 'var(--bd)',
                border: 'none', cursor: 'pointer',
                transition: 'all 0.25s var(--ease-out)',
              }}
              title={s.label}
            />
          ))}
        </div>

        <button
          data-testid="overview-next"
          onClick={goNext}
          disabled={idx === SLIDES.length - 1}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: 'none',
            color: idx === SLIDES.length - 1 ? 'var(--t3)' : 'var(--t1)',
            opacity: idx === SLIDES.length - 1 ? 0.35 : 1,
            cursor: idx === SLIDES.length - 1 ? 'default' : 'pointer',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
            padding: '8px 12px', borderRadius: 10,
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { if (idx !== SLIDES.length - 1) e.currentTarget.style.background = 'var(--sf2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          {idx < SLIDES.length - 1 && SLIDES[idx + 1].label}
          <ChevronRight size={15} />
        </button>
      </div>

      <style>{`
        .overview-scroller::-webkit-scrollbar { display: none; }
        .overview-scroller { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>
    </div>
  );
}

// ---------- Slide 0: RDV Calendly (founders) ----------
// Iter 62aj — Full-slide version of CalendlyWidget (swipeable).
function CalendlySlide() {
  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span
          aria-hidden
          style={{
            width: 34, height: 34, borderRadius: 10,
            display: 'grid', placeItems: 'center',
            background:
              'linear-gradient(140deg, #F5C56A 0%, rgba(255,255,255,0.15) 50%, #F5C56A 100%)',
            color: '#0B1220',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.25), 0 6px 14px rgba(245,197,106,0.4)',
          }}
        >
          <CalendarIconAlias size={16} strokeWidth={2.4} />
        </span>
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color: 'var(--t1)' }}>
            RDV Calendly
          </div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
            color: '#F5C56A', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 1,
          }}>Tes prochaines réservations</div>
        </div>
      </div>
      <CalendlyWidget embedded />
    </div>
  );
}

// ---------- Slide "Elevate Influence" — Iter 62ay ----------
function ElevateSlide({ nav }) {
  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span
          aria-hidden
          style={{
            width: 34, height: 34, borderRadius: 10,
            display: 'grid', placeItems: 'center',
            background: 'linear-gradient(140deg, #5BBEFC 0%, rgba(255,255,255,0.15) 50%, #1E5FFF 100%)',
            color: '#0B1220',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.25), 0 6px 14px rgba(30,95,255,0.4)',
            fontFamily: 'Sora', fontWeight: 900, fontSize: 16,
          }}
        >E</span>
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color: 'var(--t1)' }}>
            Elevate Influence School
          </div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
            color: '#5BBEFC', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 1,
          }}>Tâches maison-mère · sync live</div>
        </div>
      </div>
      <ElevateInfluenceCard onOpenAll={() => nav('/planning')} />
    </div>
  );
}




// ---------- Slide 1: Ma journée ----------
function DaySlide({ user, todayTasks, overdueCount, nav }) {
  const today = new Date();
  const dateLabel = today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const isFounderUser = (user?.role || '') === 'founder';
  const greeting = isFounderUser
    ? 'Founder, everything is under control. Keep going'
    : `Salut ${user?.name?.split(' ')[0] || ''}, prêt pour aujourd'hui ?`;
  const quote = "If you look for perfection, you'll never be content. — Leo Tolstoy";

  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Moon size={17} color="#A78BFA" strokeWidth={1.7} />
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color: 'var(--t1)' }}>
            {user?.role === 'client' ? 'Ton espace' : 'Ton agence'}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)' }}>
            {dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}
          </div>
        </div>
      </div>

      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 15.5, color: 'var(--t1)', marginBottom: 6 }}>
        {greeting}
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)',
        fontStyle: 'italic', marginBottom: 18,
      }}>
        <span style={{ opacity: 0.6 }}>“</span>
        {quote}
      </div>

      {overdueCount > 0 && (
        <button onClick={() => nav('/tasks')} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 9,
          padding: '11px 14px', borderRadius: 11, marginBottom: 18,
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
          cursor: 'pointer',
        }}>
          <AlertTriangle size={13} color="#EF4444" />
          <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: '#EF4444', flex: 1, textAlign: 'left' }}>
            {overdueCount} tâche{overdueCount > 1 ? 's' : ''} en retard
          </span>
          <ChevronRight size={13} color="#EF4444" />
        </button>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 9, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckSquare size={11} /> Tâches du jour
          </div>
          {todayTasks.length === 0 ? (
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', fontStyle: 'italic' }}>
              Aucune tâche aujourd&apos;hui
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Iter 62bt — sort by due_date ASC (urgent first) + subtasks filtered
                  so Axel's Nash project tasks don't get cut off when the raw order
                  pushed them past position 4. Cap raised to 8. */}
              {todayTasks
                .filter((t) => !t.parent_task_id)
                .slice()
                .sort((a, b) => (a.due_date || '9999-12-31').localeCompare(b.due_date || '9999-12-31'))
                .slice(0, 8)
                .map((t) => {
                const s = STAGES[t.stage] || STAGES.todo;
                const overdue = t.due_date && t.due_date.slice(0, 10) < localToday();
                return (
                  <button key={t.id} onClick={() => nav('/tasks')} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px', borderRadius: 8,
                    background: 'var(--sf2)', border: '1px solid var(--bd)',
                    textAlign: 'left', width: '100%',
                  }}>
                    <span style={{ width: 3, height: 12, borderRadius: 2, background: s.color }} />
                    <span style={{
                      flex: 1, fontFamily: 'Lexend', fontSize: 12, color: 'var(--t1)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{t.title}</span>
                    {overdue && <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: '#EF4444' }}>en retard</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 9, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={11} /> Événements
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', fontStyle: 'italic' }}>
            Aucun événement aujourd&apos;hui
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Slide 2: Objectifs ----------
function GoalsSlide({ objectives, user, nav }) {
  const weekNumber = (() => {
    const d = new Date();
    const start = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
  })();
  const groups = [
    { key: 'company',   label: 'Objectif de la boîte',   color: '#5BBEFC' },
    { key: 'founders',  label: 'Founders',               color: '#5BBEFC' },
    { key: 'gm',        label: 'General Manager',        color: '#5BBEFC' },
    { key: 'editor',    label: 'Head of Editing',        color: '#F5A623' },
    { key: 'personal',  label: 'Mes objectifs',          color: '#5BBEFC' },
  ];
  const findText = (k) => {
    if (k === 'personal') {
      const o = objectives.find((x) => x.scope === 'personal' && x.owner === user?.id);
      return o?.text;
    }
    if (k === 'gm') {
      const o = objectives.find((x) => x.scope === 'general_manager');
      return o?.text;
    }
    const o = objectives.find((x) => x.scope === k);
    return o?.text;
  };

  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 580, textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
        <Target size={20} color="var(--p)" strokeWidth={1.7} />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.45rem', color: 'var(--t1)' }}>
          Objectifs de la semaine
        </div>
      </div>
      <div className="asa-eyebrow" style={{ color: 'var(--p)', marginBottom: 22, fontSize: '0.6rem' }}>
        {new Date().getFullYear()} · Semaine {weekNumber}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, textAlign: 'left' }}>
        {groups.map((g) => {
          const text = findText(g.key);
          return (
            <div key={g.key} onClick={() => nav('/objectives')} style={{
              padding: '13px 18px',
              borderLeft: `3px solid ${g.color}`,
              borderRadius: 10,
              background: 'var(--sf2)',
              border: '1px solid var(--bd)',
              borderLeftWidth: 3, borderLeftColor: g.color,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(46,163,242,0.04)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--sf2)'; }}
            >
              <div style={{
                fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
                color: g.color, letterSpacing: '0.06em', textTransform: 'uppercase',
                marginBottom: 4,
              }}>| {g.label}</div>
              <div style={{
                fontFamily: 'Lexend', fontSize: 13.5,
                color: text ? 'var(--t1)' : 'var(--t3)',
                fontStyle: text ? 'normal' : 'italic',
              }}>
                {text || 'Aucun objectif défini pour cette semaine'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Slide 3: Tâches (big % + breakdown) ----------
function TasksSlide({ byStage, myTasks, overdueCount, nav }) {
  const total = myTasks.length;
  const done  = byStage.done || 0;
  const inProg = (byStage.todo || 0) + (byStage.in_progress || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 460, textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, marginBottom: 16 }}>
        <CheckSquare size={20} color="var(--p)" strokeWidth={1.7} />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.45rem', color: 'var(--t1)' }}>
          Tâches
        </div>
      </div>

      <div style={{
        fontFamily: 'Sora', fontWeight: 900, fontSize: '4.5rem',
        color: 'var(--p)', lineHeight: 1, marginBottom: 4,
      }}>{pct}%</div>
      <div style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', marginBottom: 26 }}>
        {done}/{total} complétées
      </div>

      {/* Mini progress bar */}
      <div style={{
        height: 4, borderRadius: 2, overflow: 'hidden',
        background: 'var(--sf2)', marginBottom: 28,
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: 'linear-gradient(90deg, var(--p), var(--pl))',
          transition: 'width 0.5s var(--ease-out)',
        }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'left' }}>
        <StatRow label="En cours"   value={inProg}     color="var(--t1)" />
        <StatRow label="Complétées" value={done}       color="var(--t1)" suffix={pct + '%'} suffixColor="var(--t3)" />
        <StatRow label="En retard"  value={overdueCount} color={overdueCount > 0 ? '#EF4444' : 'var(--t1)'} suffix={overdueCount > 0 ? 'urgent' : null} suffixColor="#EF4444" />
        <StatRow label="Total"      value={total}      color="var(--t1)" noBorder />
      </div>

      <button onClick={() => nav('/tasks')} style={{
        marginTop: 24, padding: '9px 16px', borderRadius: 999,
        background: 'rgba(46,163,242,0.1)', border: '1px solid rgba(46,163,242,0.3)',
        color: 'var(--p)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
        cursor: 'pointer',
      }}>Voir toutes les tâches →</button>
    </div>
  );
}

function StatRow({ label, value, color, suffix, suffixColor, noBorder }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '4px 0 10px',
      borderBottom: noBorder ? 'none' : '1px solid var(--bd)',
    }}>
      <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t2)' }}>
        {label}
      </span>
      <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 19, color, lineHeight: 1 }}>{value}</span>
      {suffix && (
        <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: suffixColor || 'var(--t3)' }}>
          {suffix}
        </span>
      )}
    </div>
  );
}

// ---------- Slide 5: Messages (Iter47f) ----------
function MessagesSlide({ nav }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/conversations')
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : r.data?.conversations || [];
        setConversations(items.filter((c) => !c.archived && c.type !== 'dm').slice(0, 8));
      })
      .catch(() => setConversations([]))
      .finally(() => setLoading(false));
  }, []);

  const totalUnread = conversations.reduce((n, c) => n + (c.unread_count || 0), 0);

  return (
    <div className="fade-up" style={{ maxWidth: 640, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <MessageCircle size={28} color="#5BBEFC" strokeWidth={1.6} />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontFamily: 'Sora', fontWeight: 800, fontSize: '1.5rem', color: 'var(--t1)', letterSpacing: '-0.01em' }}>
            Messages
          </h2>
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
            {conversations.length} conversation{conversations.length > 1 ? 's' : ''}
            {totalUnread > 0 && ` · ${totalUnread} non lu${totalUnread > 1 ? 's' : ''}`}
          </div>
        </div>
        <button
          data-testid="msg-slide-open"
          onClick={() => nav('/messages')}
          className="asa-btn"
          style={{ padding: '8px 14px', fontSize: 12 }}
        >Ouvrir</button>
      </div>

      {loading ? (
        <div className="shimmer" style={{ height: 200, borderRadius: 14 }} />
      ) : conversations.length === 0 ? (
        <div style={{
          padding: 30, textAlign: 'center', borderRadius: 14,
          background: 'var(--sf)', border: '1.5px dashed var(--bd)',
          fontFamily: 'Lexend', fontSize: 13, color: 'var(--t3)',
        }}>
          Aucune conversation pour l&apos;instant.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {conversations.map((c) => (
            <button
              key={c.id}
              data-testid={`msg-slide-conv-${c.id}`}
              onClick={() => nav(`/messages?conv=${c.id}`)}
              className="asa-card row-card"
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 14px', width: '100%', textAlign: 'left',
                cursor: 'pointer', transition: 'transform 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
            >
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: 'rgba(91,190,252,0.14)', border: '1.5px solid #5BBEFC55',
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <MessageCircle size={14} color="#5BBEFC" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{c.name || 'Sans titre'}</div>
                <div style={{
                  fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 1,
                }}>
                  {c.message_count || 0} message{(c.message_count || 0) > 1 ? 's' : ''}
                </div>
              </div>
              {(c.unread_count || 0) > 0 && (
                <span style={{
                  padding: '2px 8px', borderRadius: 999,
                  background: 'var(--r)', color: '#fff',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
                }}>{c.unread_count}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------- Slide 4: Calendrier ----------
function CalendarSlide({ myTasks, nav }) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const monthLabel = today.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = (firstDay.getDay() || 7) - 1; // Mon=0

  const taskDays = new Set();
  myTasks.forEach((t) => {
    if (t.due_date) {
      const d = parseLocalDate(t.due_date);
      if (d && d.getFullYear() === year && d.getMonth() === month) {
        taskDays.add(d.getDate());
      }
    }
  });

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="fade-up" style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 22 }}>
        <Calendar size={20} color="var(--p)" strokeWidth={1.7} />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color: 'var(--t1)', textTransform: 'capitalize' }}>
          {monthLabel}
        </div>
      </div>

      <div style={{
        background: 'rgba(46,163,242,0.04)',
        border: '1px solid var(--bd)', borderRadius: 14,
        padding: 18, display: 'inline-block',
      }}>
        {/* Day-of-week header */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(7, 38px)', gap: 4, marginBottom: 8,
        }}>
          {['L','M','M','J','V','S','D'].map((d, i) => (
            <div key={i} style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
              color: 'var(--t3)', textAlign: 'center',
            }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 38px)', gap: 4 }}>
          {cells.map((d, i) => {
            const isToday = d === today.getDate();
            const hasTask = d && taskDays.has(d);
            return (
              <div key={i} style={{
                width: 38, height: 38, borderRadius: 9,
                display: 'grid', placeItems: 'center', position: 'relative',
                background: isToday ? 'var(--p)' : 'transparent',
                color: isToday ? '#fff' : (d ? 'var(--t1)' : 'transparent'),
                fontFamily: 'Sora', fontWeight: isToday ? 800 : 600, fontSize: 12.5,
              }}>
                {d}
                {hasTask && !isToday && (
                  <span style={{
                    position: 'absolute', bottom: 4, width: 4, height: 4, borderRadius: '50%',
                    background: 'var(--p)',
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 22 }}>
        {taskDays.size === 0 ? 'Aucun événement ce mois-ci' : `${taskDays.size} jour${taskDays.size > 1 ? 's' : ''} avec tâches`}
      </div>

      <button onClick={() => nav('/planning')} style={{
        marginTop: 18, padding: '9px 16px', borderRadius: 999,
        background: 'rgba(46,163,242,0.1)', border: '1px solid rgba(46,163,242,0.3)',
        color: 'var(--p)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
        cursor: 'pointer',
      }}>Voir le calendrier complet →</button>
    </div>
  );
}


// ============================================================================
// MONTEUR SLIDES
// ============================================================================

// ---------- Slide M1: Mon statut ----------
function MyStatusSlide({ user, available, onToggleAvailable, todayTasks, myTasks, overdueCount }) {
  const isAvail = available !== false;
  const today = new Date();
  const dateLabel = today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const greeting = isAvail
    ? `Salut ${user?.name?.split(' ')[0] || ''}, prêt pour aujourd'hui ?`
    : `Tu es marqué indisponible. Reviens quand tu peux reprendre.`;
  const activeCount = myTasks.filter((t) => t.stage !== 'done').length;

  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 600 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Briefcase size={17} color={isAvail ? '#34D399' : '#EF4444'} strokeWidth={1.7} />
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color: 'var(--t1)' }}>Mon statut</div>
          <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', textTransform: 'capitalize' }}>{dateLabel}</div>
        </div>
      </div>
      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 15.5, color: 'var(--t1)', marginBottom: 18 }}>{greeting}</div>
      <div style={{
        padding: 18, borderRadius: 14,
        background: isAvail ? 'linear-gradient(135deg, rgba(52,211,153,0.14), transparent 70%)' : 'linear-gradient(135deg, rgba(239,68,68,0.14), transparent 70%)',
        border: `1.5px solid ${isAvail ? 'rgba(52,211,153,0.45)' : 'rgba(239,68,68,0.45)'}`,
        display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: isAvail ? 'rgba(52,211,153,0.2)' : 'rgba(239,68,68,0.2)',
          border: `1.5px solid ${isAvail ? '#34D399' : '#EF4444'}`,
          display: 'grid', placeItems: 'center',
        }}>
          {isAvail ? <Check size={22} color="#34D399" strokeWidth={2.5} /> : <X size={22} color="#EF4444" strokeWidth={2.5} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 16, color: isAvail ? '#34D399' : '#EF4444' }}>
            {isAvail ? 'Disponible' : 'Indisponible'}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
            {isAvail ? 'Visible par les founders comme prêt à recevoir une mission' : 'Visible par les founders comme bloqué'}
          </div>
        </div>
        <button data-testid="monteur-avail-toggle" onClick={() => onToggleAvailable(!isAvail)}
          style={{
            padding: '9px 14px', borderRadius: 999,
            background: isAvail ? 'rgba(239,68,68,0.13)' : 'var(--p)',
            border: `1.5px solid ${isAvail ? 'rgba(239,68,68,0.4)' : 'var(--pl)'}`,
            color: isAvail ? '#EF4444' : '#fff',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}>{isAvail ? 'Passer indispo' : 'Devenir dispo'}</button>
      </div>
      {/* Iter43 — Compteurs de tâches retirés (anti-anxiété : user's request) */}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 11, background: 'var(--sf2)', border: '1px solid var(--bd)', textAlign: 'center' }}>
      <div style={{ fontFamily: 'Sora', fontWeight: 900, fontSize: 26, color, lineHeight: 1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );
}

// ---------- Slide M2: Mes vidéos en cours ----------
function MyVideosSlide({ myTasks, projects, nav }) {
  const videoTasks = useMemo(() => myTasks.filter((t) => {
    const tags = Array.isArray(t.tags) ? t.tags : [];
    const isVideo = t.kind === 'video' || tags.includes('video_deliverable') || t.event_type === 'content' || !!t.concept_id;
    return isVideo && t.stage !== 'done' && !t.archived;
  }), [myTasks]);
  const computeBlock = (t) => {
    if (t.publication_done) return { idx: 4, label: 'Publication ✓', pct: 100, color: '#34D399' };
    if (t.concept_validated) return { idx: 4, label: 'Publication', pct: 75, color: '#A78BFA' };
    if (t.tech_validated)    return { idx: 3, label: 'Vidéo montée', pct: 50, color: '#5BBEFC' };
    if (t.consignes_validated) return { idx: 2, label: 'Consignes tech', pct: 25, color: '#F5A623' };
    return { idx: 1, label: 'Consignes', pct: 5, color: '#EC4899' };
  };
  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 580 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <Sparkles size={20} color="var(--p)" strokeWidth={1.7} />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color: 'var(--t1)' }}>Mes vidéos en cours</div>
      </div>
      {videoTasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t2)', marginBottom: 6 }}>Aucune vidéo en cours</div>
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)' }}>Repose-toi un peu, ou attends qu&apos;un founder te dispatch.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {videoTasks.slice(0, 5).map((t) => {
            const blk = computeBlock(t);
            const p = projects?.find((x) => x.id === t.project_id);
            return (
              <button key={t.id} onClick={() => nav('/tasks')}
                style={{ padding: '12px 14px', borderRadius: 11, textAlign: 'left', width: '100%', background: 'var(--sf2)', border: '1.5px solid var(--bd)', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  {p && (
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      background: `${p.color || '#5BBEFC'}1F`, border: `1px solid ${p.color || '#5BBEFC'}55`,
                      display: 'grid', placeItems: 'center',
                    }}>
                      <ProjectIcon name={p.icon || 'camera'} size={11} color={p.color || '#5BBEFC'} />
                    </div>
                  )}
                  <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                  <span style={{ padding: '2px 8px', borderRadius: 999, background: `${blk.color}1F`, border: `1px solid ${blk.color}55`, fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5, color: blk.color }}>Bloc {blk.idx} · {blk.label}</span>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: 'var(--bd)', overflow: 'hidden' }}>
                  <div style={{ width: `${blk.pct}%`, height: '100%', background: blk.color, transition: 'width 0.4s' }} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Slide M3: Mes projets ----------
function MyProjectsSlide({ projects, myTasks, user, nav }) {
  const myProjects = (projects || []).filter((p) => (p.assignees || []).includes(user?.id));
  return (
    <div className="fade-up" style={{ width: '100%', maxWidth: 580 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <Briefcase size={20} color="var(--p)" strokeWidth={1.7} />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.4rem', color: 'var(--t1)' }}>Mes projets</div>
        <span style={{ marginLeft: 4, fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)' }}>· {myProjects.length}</span>
      </div>
      {myProjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t2)', marginBottom: 6 }}>Aucun projet pour le moment</div>
          <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)' }}>Les founders ne t&apos;ont pas encore mis sur un projet.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {myProjects.slice(0, 4).map((p) => {
            const projTasks = myTasks.filter((t) => t.project_id === p.id);
            const done = projTasks.filter((t) => t.stage === 'done').length;
            const pct = projTasks.length > 0 ? Math.round((done / projTasks.length) * 100) : 0;
            return (
              <button key={p.id} onClick={() => { localStorage.setItem('asa_open_project', p.id); nav('/projects'); }}
                style={{ padding: '13px 14px', borderRadius: 12, textAlign: 'left', width: '100%', background: 'var(--sf2)', border: `1.5px solid ${p.color || '#5BBEFC'}33`, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: `${p.color || '#5BBEFC'}1F`, border: `1px solid ${p.color || '#5BBEFC'}55`, display: 'grid', placeItems: 'center' }}>
                    <ProjectIcon name={p.icon || 'camera'} size={14} color={p.color || '#5BBEFC'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13.5, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 1 }}>{projTasks.length} tâche{projTasks.length > 1 ? 's' : ''} · {done} faite{done > 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: p.color || '#5BBEFC' }}>{pct}%</div>
                </div>
                <div style={{ height: 4, borderRadius: 2, background: 'var(--bd)', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: p.color || '#5BBEFC', transition: 'width 0.4s' }} />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
